/**
 * ============================================================================
 * USE LINKS HOOK
 * ============================================================================
 * 
 * Custom hook for transaction link generation and management
 */

import { useState, useCallback } from 'react';
import * as linksService from '../services/links';

export const useLinks = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shortLink, setShortLink] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [socialSharingLinks, setSocialSharingLinks] = useState(null);
  const [linkStats, setLinkStats] = useState(null);

  /**
   * Generate short link
   */
  const generateShortLink = useCallback(async (transactionId, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await linksService.generateShortLink(transactionId, options);
      setShortLink(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to generate short link');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get short link
   */
  const getShortLink = useCallback(async (shortCode) => {
    try {
      setLoading(true);
      setError(null);
      const response = await linksService.getShortLink(shortCode);
      setShortLink(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get short link');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get QR code
   */
  const getQRCode = useCallback(async (shortCode, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await linksService.getQRCode(shortCode, options);
      setQrCode(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get QR code');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get social sharing links
   */
  const getSocialSharingLinks = useCallback(async (shortCode) => {
    try {
      setLoading(true);
      setError(null);
      const response = await linksService.getSocialSharingLinks(shortCode);
      setSocialSharingLinks(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get social sharing links');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Track link view
   */
  const trackLinkView = useCallback(async (shortCode, metadata = {}) => {
    try {
      await linksService.trackLinkView(shortCode, metadata);
    } catch (err) {
      console.error('Failed to track link view:', err);
    }
  }, []);

  /**
   * Track link share
   */
  const trackLinkShare = useCallback(async (shortCode, platform, metadata = {}) => {
    try {
      await linksService.trackLinkShare(shortCode, platform, metadata);
    } catch (err) {
      console.error('Failed to track link share:', err);
    }
  }, []);

  /**
   * Regenerate short link
   */
  const regenerateShortLink = useCallback(async (transactionId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await linksService.regenerateShortLink(transactionId);
      setShortLink(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to regenerate short link');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get link statistics
   */
  const getLinkStats = useCallback(async (shortCode) => {
    try {
      setLoading(true);
      setError(null);
      const response = await linksService.getLinkStats(shortCode);
      setLinkStats(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get link statistics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Copy link to clipboard
   */
  const copyLinkToClipboard = useCallback(async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      return true;
    } catch (err) {
      console.error('Failed to copy link to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return true;
      } catch (fallbackErr) {
        document.body.removeChild(textArea);
        throw fallbackErr;
      }
    }
  }, []);

  /**
   * Share link via Web Share API
   */
  const shareLink = useCallback(async (link, title = '', text = '') => {
    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url: link,
        });
        return true;
      } else {
        // Fallback to clipboard
        await copyLinkToClipboard(link);
        return false;
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Failed to share link:', err);
        // Fallback to clipboard
        await copyLinkToClipboard(link);
      }
      return false;
    }
  }, [copyLinkToClipboard]);

  return {
    // State
    shortLink,
    qrCode,
    socialSharingLinks,
    linkStats,
    loading,
    error,
    
    // Actions
    generateShortLink,
    getShortLink,
    getQRCode,
    getSocialSharingLinks,
    trackLinkView,
    trackLinkShare,
    regenerateShortLink,
    getLinkStats,
    copyLinkToClipboard,
    shareLink,
  };
};

export default useLinks;
